import type { CustomizationDraft, Key, PiclickWP } from '@/types/piclick';

export const LETTER_SYMBOL_ID = 'letter';

export function emptyKey(): Key {
  return { symbolId: null, letterValue: '', colorId: null };
}

export function createInitialDraft(): CustomizationDraft {
  return {
    sizeId: null,
    baseColorId: null,
    keys: [],
    accessoryId: null,
  };
}

export function resizeKeys(keys: Key[], target: number): Key[] {
  const next = keys.slice(0, target);
  while (next.length < target) {
    next.push(emptyKey());
  }
  return next;
}

export function calculateTotal(draft: CustomizationDraft, options: PiclickWP): number {
  const size = options.sizes.find((s) => s.id === draft.sizeId);
  const accessory = options.accessories.find((a) => a.id === draft.accessoryId);
  return (size?.price ?? 0) + (accessory?.price ?? 0);
}

export function isKeyComplete(key: Key): boolean {
  if (!key.symbolId || !key.colorId) return false;
  if (key.symbolId === LETTER_SYMBOL_ID) {
    return /^[A-Z]$/.test(key.letterValue);
  }
  return true;
}

export function isCustomizationComplete(draft: CustomizationDraft): boolean {
  if (!draft.sizeId || !draft.baseColorId || !draft.accessoryId) return false;
  if (draft.keys.length === 0) return false;
  return draft.keys.every(isKeyComplete);
}

export function getMissingItems(draft: CustomizationDraft): string[] {
  const missing: string[] = [];
  if (!draft.sizeId) missing.push('tamanho');
  if (!draft.baseColorId) missing.push('cor da base');
  if (draft.keys.length > 0) {
    const incomplete = draft.keys.filter((k) => !isKeyComplete(k)).length;
    if (incomplete > 0) {
      missing.push(`${incomplete} tecla${incomplete > 1 ? 's' : ''}`);
    }
  }
  if (!draft.accessoryId) missing.push('acessório');
  return missing;
}

export type WizardStep = 'base' | 'keys' | 'accessory' | 'review';

export const WIZARD_STEPS: WizardStep[] = ['base', 'keys', 'accessory', 'review'];

export function isStepValid(step: WizardStep, draft: CustomizationDraft): boolean {
  switch (step) {
    case 'base':
      return !!draft.sizeId && !!draft.baseColorId;
    case 'keys':
      return draft.keys.length > 0 && draft.keys.every(isKeyComplete);
    case 'accessory':
      return !!draft.accessoryId;
    case 'review':
      return isCustomizationComplete(draft);
  }
}

export function getStepBlocker(step: WizardStep, draft: CustomizationDraft): string | null {
  switch (step) {
    case 'base':
      if (!draft.sizeId && !draft.baseColorId) return 'Escolha um tamanho e uma cor.';
      if (!draft.sizeId) return 'Escolha um tamanho.';
      if (!draft.baseColorId) return 'Escolha uma cor da base.';
      return null;
    case 'keys': {
      if (draft.keys.length === 0) return 'Volte e escolha um tamanho primeiro.';
      const incomplete = draft.keys.filter((k) => !isKeyComplete(k)).length;
      if (incomplete > 0) {
        return `Falta${incomplete > 1 ? 'm' : ''} configurar ${incomplete} tecla${
          incomplete > 1 ? 's' : ''
        }.`;
      }
      return null;
    }
    case 'accessory':
      return draft.accessoryId ? null : 'Escolha um acessório.';
    case 'review':
      return null;
  }
}
